import { useEffect, useState, type CSSProperties } from "react";

import { addBusinessDays } from "../../../utils/date";

import type { InteractionProps } from "./interfaces";

export type InteractionListEditContext = {
  onPatch: (
    interactionId: number,
    body: Record<string, unknown>,
    field:
      | "status"
      | "duracao"
      | "paymentDay"
      | "feeCredito"
      | "paymentDeadline"
  ) => Promise<void>;
  patchingFieldKey: string | null;
};

const STATUS_OPTIONS: { value: number; label: string }[] = [
  { value: 1, label: "Not Done" },
  { value: 2, label: "Done" },
  { value: 3, label: "Canceled Client" },
  { value: 4, label: "On Hold" },
  { value: 5, label: "Reagendar" },
  { value: 6, label: "No show client" },
  { value: 7, label: "No show expert" },
  { value: 8, label: "Canceled Expert" },
];

const LABEL_BY_STATUS_CODE = Object.fromEntries(
  STATUS_OPTIONS.map(({ value, label }) => [value, label])
) as Record<number, string>;

export function statusCodeFromInteraction(doc: InteractionProps): number {
  if (doc.statusCode != null && Number.isFinite(Number(doc.statusCode))) {
    return Number(doc.statusCode);
  }
  const label = String(doc.status ?? "").trim();
  const found = STATUS_OPTIONS.find((o) => o.label === label);
  return found?.value ?? 1;
}

function feeToInputString(value: number | null | undefined): string {
  if (value == null || Number.isNaN(Number(value))) return "";
  const n = Number(value);
  if (n === 0) return "0";
  return String(n);
}

function parseDecimalFeeInput(raw: string): number | null | "invalid" {
  const t = String(raw).trim().replace(/\s/g, "").replace(",", ".");
  if (t === "") return null;
  const n = parseFloat(t);
  if (!Number.isFinite(n)) return "invalid";
  return n;
}

function cents(n: number): number {
  return Math.round(n * 100);
}

function deadlineToDateInputValue(dateValue: string | undefined): string {
  const s = String(dateValue ?? "").trim();
  if (!s || s === "-") return "";
  const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
  if (m) return m[1];
  const t = Date.parse(s);
  if (!Number.isNaN(t)) {
    const d = new Date(t);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
      2,
      "0"
    )}-${String(d.getDate()).padStart(2, "0")}`;
  }
  return "";
}

const cellControlStyle: CSSProperties = {
  maxWidth: "100%",
  fontSize: "inherit",
  fontFamily: "inherit",
  boxSizing: "border-box",
};

export function InteractionStatusCell({
  doc,
  ctx,
}: {
  doc: InteractionProps;
  ctx: InteractionListEditContext;
}) {
  const code = statusCodeFromInteraction(doc);
  const key = `${doc.id}-status`;
  const disabled = ctx.patchingFieldKey === key;

  return (
    <select
      value={code}
      disabled={disabled}
      title={LABEL_BY_STATUS_CODE[code] ?? doc.status}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onChange={async (e) => {
        const next = Number(e.target.value);
        if (!Number.isFinite(next) || next === code) return;

        const body: Record<string, unknown> = { status: next };
        if (next === 2) {
          const meetingDate = deadlineToDateInputValue(doc.data);
          if (meetingDate) {
            body.payment_deadline = addBusinessDays(meetingDate, 10);
          }
        }

        await ctx.onPatch(doc.id, body, "status");
      }}
      style={{
        ...cellControlStyle,
        minWidth: 0,
        width: "100%",
        height: 28,
        padding: "2px 6px",
        borderRadius: 6,
        border: "1px solid #c9d8e1",
        background: "#fff",
        color: "#000",
        cursor: disabled ? "wait" : "pointer",
      }}
    >
      {STATUS_OPTIONS.map(({ value, label }) => (
        <option key={value} value={value}>
          {label}
        </option>
      ))}
    </select>
  );
}

export function InteractionDurationCell({
  doc,
  ctx,
}: {
  doc: InteractionProps;
  ctx: InteractionListEditContext;
}) {
  const [local, setLocal] = useState(String(doc.duracao ?? ""));
  const key = `${doc.id}-duracao`;
  const disabled = ctx.patchingFieldKey === key;

  useEffect(() => {
    setLocal(String(doc.duracao ?? ""));
  }, [doc.id, doc.duracao]);

  const commit = async () => {
    const trimmed = local.trim();
    if (trimmed === "") {
      if (doc.duracao == null) return;
      await ctx.onPatch(doc.id, { duration: "" }, "duracao");
      return;
    }
    const parsed = Number(trimmed);
    if (Number.isNaN(parsed) || parsed < 0) {
      setLocal(String(doc.duracao ?? ""));
      return;
    }
    if (parsed === Number(doc.duracao)) return;
    await ctx.onPatch(doc.id, { duration: parsed }, "duracao");
  };

  return (
    <input
      type="number"
      min={0}
      step={1}
      value={local}
      disabled={disabled}
      title="Duração (min)"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onChange={(e) => setLocal(e.target.value)}
      onBlur={() => void commit()}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          (e.target as HTMLInputElement).blur();
        }
        e.stopPropagation();
      }}
      style={{
        ...cellControlStyle,
        width: 72,
        height: 28,
        padding: "2px 6px",
        borderRadius: 6,
        border: "1px solid #c9d8e1",
        background: "#fff",
        color: "#000",
      }}
    />
  );
}

export function InteractionFeeCell({
  doc,
  ctx,
}: {
  doc: InteractionProps;
  ctx: InteractionListEditContext;
}) {
  const [local, setLocal] = useState(feeToInputString(doc.feeCredito));
  const key = `${doc.id}-feeCredito`;
  const disabled = ctx.patchingFieldKey === key;

  useEffect(() => {
    setLocal(feeToInputString(doc.feeCredito));
  }, [doc.id, doc.feeCredito]);

  const commit = async () => {
    const parsed = parseDecimalFeeInput(local);
    if (parsed === "invalid") {
      setLocal(feeToInputString(doc.feeCredito));
      return;
    }
    const prevNum = Number(doc.feeCredito ?? 0);
    if (parsed === null) {
      if (prevNum === 0) return;
      await ctx.onPatch(doc.id, { fee: null }, "feeCredito");
      return;
    }
    if (parsed < 0) {
      setLocal(feeToInputString(doc.feeCredito));
      return;
    }
    if (cents(parsed) === cents(prevNum)) return;
    await ctx.onPatch(doc.id, { fee: parsed }, "feeCredito");
  };

  return (
    <input
      type="text"
      inputMode="decimal"
      autoComplete="off"
      value={local}
      disabled={disabled}
      title="Fee (valor decimal)"
      placeholder="0,00"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onChange={(e) => setLocal(e.target.value)}
      onBlur={() => void commit()}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          (e.target as HTMLInputElement).blur();
        }
        e.stopPropagation();
      }}
      style={{
        ...cellControlStyle,
        width: "100%",
        minWidth: 72,
        maxWidth: 120,
        height: 28,
        padding: "2px 6px",
        borderRadius: 6,
        border: "1px solid #c9d8e1",
        background: "#fff",
        color: "#000",
      }}
    />
  );
}

export function InteractionPaymentDayCell({
  doc,
  ctx,
}: {
  doc: InteractionProps;
  ctx: InteractionListEditContext;
}) {
  const value = deadlineToDateInputValue(doc.paymentDay);
  const key = `${doc.id}-paymentDay`;
  const disabled = ctx.patchingFieldKey === key;

  return (
    <input
      type="date"
      value={value}
      disabled={disabled}
      title="Data de pagamento"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onChange={async (e) => {
        const next = e.target.value;
        const prev = deadlineToDateInputValue(doc.paymentDay);
        if (next === prev) return;
        await ctx.onPatch(doc.id, { payment_day: next || "" }, "paymentDay");
      }}
      style={{
        ...cellControlStyle,
        width: "100%",
        minWidth: 0,
        height: 28,
        padding: "2px 4px",
        borderRadius: 6,
        border: "1px solid #c9d8e1",
        background: "#fff",
        color: "#000",
      }}
    />
  );
}
